import React, { useState, useEffect, useRef } from "react";
import BackupService, {
  type BackupConfig,
  type BackupEntry,
} from "../../services/BackupService";
import { BackupHeader } from "./backup/BackupHeader";
import { BackupStatus } from "./backup/BackupStatus";
import { BackupScheduleInfo } from "./backup/BackupScheduleInfo";
import { BackupsList } from "./backup/BackupsList";
import { RestoreConfirmModal } from "./backup/RestoreConfirmModal";
import { Button } from "../ui/Button";

interface BackupManagerPanelProps {
  inModal?: boolean;
}

export const BackupManagerPanel: React.FC<BackupManagerPanelProps> = ({
  inModal = false,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [backups, setBackups] = useState(BackupService.getAvailableBackups());
  const [config, setConfig] = useState<BackupConfig | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedBackupId, setSelectedBackupId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize backup service
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const initialized = await BackupService.initialize();
        setIsInitialized(initialized);

        if (initialized) {
          BackupService.getBackups();
          BackupService.getConfig();
        }
      } catch (error) {
        setError("Failed to initialize backup service");
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    init();

    // Set up refresh interval
    const refreshInterval = setInterval(() => {
      if (isInitialized) {
        BackupService.getBackups();
        BackupService.getConfig();
      }
    }, 10000);

    return () => clearInterval(refreshInterval);
  }, [isInitialized]);

  // Subscribe to backup service updates
  useEffect(() => {
    const handleStorageChange = () => {
      setBackups(BackupService.getAvailableBackups());
      setConfig(BackupService.getCurrentConfig());
    };

    // Initial load
    handleStorageChange();

    // Listen for changes
    window.addEventListener("storage", handleStorageChange);

    // Refresh at regular intervals
    const intervalId = setInterval(handleStorageChange, 5000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  // Handle creating a backup
  const handleCreateBackup = async () => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const success = await BackupService.createBackup();
      if (success) {
        setSuccessMessage("Backup created successfully");
        BackupService.getBackups();
      } else {
        setError("Failed to create backup");
      }
    } catch (error) {
      setError("An error occurred while creating the backup");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle restoring a backup
  const handleRestoreBackup = async () => {
    if (!selectedBackupId) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const success = await BackupService.restoreBackup(selectedBackupId);
      if (success) {
        setSuccessMessage(
          "Backup restored successfully. Reloading application..."
        );
        setShowRestoreModal(false);
      } else {
        setError("Failed to restore backup");
      }
    } catch (error) {
      setError("An error occurred while restoring the backup");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle downloading a backup
  const handleDownloadBackup = (backup: BackupEntry) => {
    BackupService.downloadBackup(backup);
  };

  // Handle deleting a backup
  const handleDeleteBackup = async (backupId: string) => {
    if (!confirm("Are you sure you want to delete this backup?")) return;

    setIsLoading(true);
    setError(null);

    try {
      const success = await BackupService.deleteBackup(backupId);
      if (success) {
        BackupService.getBackups();
      } else {
        setError("Failed to delete backup");
      }
    } catch (error) {
      setError("An error occurred while deleting the backup");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle updating config
  const handleUpdateConfig = async (newConfig: BackupConfig) => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await BackupService.setConfig(newConfig);
      if (success) {
        setSuccessMessage("Configuration updated successfully");
        BackupService.getConfig();
      } else {
        setError("Failed to update configuration");
      }
    } catch (error) {
      setError("An error occurred while updating the configuration");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file system access request
  const handleRequestFileSystemAccess = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await BackupService.requestFileSystemAccess();
      if (success) {
        setSuccessMessage("File system access granted");
        BackupService.getConfig();
      } else {
        setError("Failed to get file system access");
      }
    } catch (error) {
      setError("An error occurred while requesting file system access");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle importing a backup file
  const handleImportBackup = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection for import
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const success = await BackupService.importBackupFromFile(file);
      if (success) {
        setSuccessMessage(
          "Backup imported successfully. Reloading application..."
        );
      } else {
        setError("Failed to import backup");
      }
    } catch (error) {
      setError("An error occurred while importing the backup");
      console.error(error);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className={inModal ? "" : "overflow-auto"}>
      {!isInitialized ? (
        <div className="text-center p-4">
          <p className="text-gray-500">Initializing backup service...</p>
        </div>
      ) : (
        <>
          <BackupHeader
            onCreateBackup={handleCreateBackup}
            onImportBackup={handleImportBackup}
            isLoading={isLoading}
            showTitle={!inModal}
          />

          <input
            type="file"
            ref={fileInputRef}
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />

          <BackupStatus
            error={error}
            successMessage={successMessage}
            isLoading={isLoading}
          />

          {/* Backup Configuration Section */}
          {config && (
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Backup Configuration
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Backup Frequency
                  </label>
                  <select
                    value={config.frequency}
                    onChange={(e) =>
                      handleUpdateConfig({
                        ...config,
                        frequency: e.target.value as BackupConfig["frequency"],
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  >
                    <option value="manual">Manual Only</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Backups to Keep
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={config.maxBackups}
                    onChange={(e) =>
                      handleUpdateConfig({
                        ...config,
                        maxBackups: parseInt(e.target.value),
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    id="auto-download"
                    type="checkbox"
                    checked={config.autoDownload}
                    onChange={(e) =>
                      handleUpdateConfig({
                        ...config,
                        autoDownload: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="auto-download"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Automatically download backups when created
                  </label>
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <input
                      id="use-file-system"
                      type="checkbox"
                      checked={config.useFileSystem}
                      onChange={(e) =>
                        handleUpdateConfig({
                          ...config,
                          useFileSystem: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    <label
                      htmlFor="use-file-system"
                      className="ml-2 text-sm text-gray-700"
                    >
                      Save backups to local folder automatically
                    </label>
                  </div>

                  {config.useFileSystem && (
                    <div className="ml-6 space-y-2">
                      {config.fileSystemHandle ? (
                        <div className="text-sm text-gray-600 flex items-center justify-between">
                          <span>
                            Current folder:{" "}
                            <strong>{config.fileSystemHandle.name}</strong>
                          </span>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleRequestFileSystemAccess}
                            disabled={isLoading}
                          >
                            Change Folder
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleRequestFileSystemAccess}
                          disabled={isLoading}
                        >
                          Select Folder
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-200">
                {"showDirectoryPicker" in window ? (
                  <div className="text-xs text-gray-500 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Your browser supports direct folder access for automatic
                    backups.
                  </div>
                ) : (
                  <div className="text-xs text-orange-500 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Your browser doesn't support the File System Access API.
                    Backups will use downloads instead.
                  </div>
                )}
              </div>
            </div>
          )}

          <BackupScheduleInfo config={config} />

          <BackupsList
            backups={backups}
            onRestoreBackup={(id) => {
              setSelectedBackupId(id);
              setShowRestoreModal(true);
            }}
            onDownloadBackup={handleDownloadBackup}
            onDeleteBackup={handleDeleteBackup}
          />
        </>
      )}

      <RestoreConfirmModal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        onConfirm={handleRestoreBackup}
      />
    </div>
  );
};
